import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import { prepareFilters, preparePagination } from '@/utils/apiParamsUtils';

const API_BASE = '/financial-statements';

// ============================================
// Financial Statements API
// ============================================

/**
 * Get all financial statements with filters
 * @param {Object} params - Query parameters (filters, pagination)
 * @returns {Promise<Object>} Statements and pagination info
 */
export const getFinancialStatements = async (params = {}) => {
  try {
    const filters = prepareFilters(params, {
      company: 'default',
      year: 'default',
      status: 'default',
      companySize: 'default',
    });

    const pagination = preparePagination(params);
    const queryParams = qs.stringify({ ...filters, ...pagination });

    const response = await axiosClient.get(`${API_BASE}?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get financial statements:', error);
    throw error;
  }
};

/**
 * Get financial statement by ID
 * @param {string} statementId - Statement ID
 * @returns {Promise<Object>} Statement details
 */
export const getFinancialStatementById = async (statementId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/${statementId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get financial statement:', error);
    throw error;
  }
};

/**
 * Get financial statement by company and year
 * @param {string} companyId - Company ID
 * @param {number} year - Reporting year
 * @returns {Promise<Object>} Statement or null
 */
export const getStatementByCompanyYear = async (companyId, year) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/company/${companyId}/year/${year}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get statement by company/year:', error);
    throw error;
  }
};

/**
 * Upload Excel and get preview
 * @param {FormData} formData - FormData with file, companyId, year, companySize
 * @returns {Promise<Object>} Preview data
 */
export const uploadAndPreview = async (formData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutes for parsing
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload and preview:', error);
    throw error;
  }
};

/**
 * Create or update financial statement
 * @param {Object} data - Statement data
 * @returns {Promise<Object>} Created/updated statement
 */
export const createOrUpdateStatement = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create/update statement:', error);
    throw error;
  }
};

/**
 * Update statement status
 * @param {string} statementId - Statement ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated statement
 */
export const updateStatementStatus = async (statementId, status) => {
  try {
    const response = await axiosClient.patch(`${API_BASE}/${statementId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Failed to update statement status:', error);
    throw error;
  }
};

/**
 * Delete financial statement
 * @param {string} statementId - Statement ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteFinancialStatement = async (statementId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/${statementId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete financial statement:', error);
    throw error;
  }
};

/**
 * Get companies dropdown
 * @returns {Promise<Object>} Companies list
 */
export const getCompaniesDropdown = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/companies/dropdown`);
    return response.data;
  } catch (error) {
    console.error('Failed to get companies dropdown:', error);
    throw error;
  }
};

// ============================================
// Enums API
// ============================================

/**
 * Get company sizes enum
 * @returns {Promise<Object>} Company sizes
 */
export const getCompanySizesEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/company-sizes`);
    return response.data;
  } catch (error) {
    console.error('Failed to get company sizes enum:', error);
    throw error;
  }
};

/**
 * Get statuses enum
 * @returns {Promise<Object>} Statuses
 */
export const getStatusesEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/statuses`);
    return response.data;
  } catch (error) {
    console.error('Failed to get statuses enum:', error);
    throw error;
  }
};

/**
 * Get form types enum
 * @returns {Promise<Object>} Form types
 */
export const getFormTypesEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/form-types`);
    return response.data;
  } catch (error) {
    console.error('Failed to get form types enum:', error);
    throw error;
  }
};

/**
 * Get validation severities enum
 * @returns {Promise<Object>} Validation severities
 */
export const getValidationSeveritiesEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/validation-severities`);
    return response.data;
  } catch (error) {
    console.error('Failed to get validation severities enum:', error);
    throw error;
  }
};

/**
 * Get validation operators enum
 * @returns {Promise<Object>} Validation operators
 */
export const getValidationOperatorsEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/validation-operators`);
    return response.data;
  } catch (error) {
    console.error('Failed to get validation operators enum:', error);
    throw error;
  }
};

// ============================================
// AOP Mappings API
// ============================================

/**
 * Get all AOP mappings
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Mappings and pagination
 */
export const getAopMappings = async (params = {}) => {
  try {
    const queryParams = qs.stringify(params);
    const response = await axiosClient.get(`${API_BASE}/mappings?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get AOP mappings:', error);
    throw error;
  }
};

/**
 * Get active mapping by company size and form type
 * @param {string} companySize - Company size
 * @param {string} formType - Form type
 * @returns {Promise<Object>} Active mapping
 */
export const getActiveMapping = async (companySize, formType) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/mappings/active/${companySize}/${formType}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get active mapping:', error);
    throw error;
  }
};

/**
 * Get AOP mapping by ID
 * @param {string} mappingId - Mapping ID
 * @returns {Promise<Object>} Mapping details
 */
export const getAopMappingById = async (mappingId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/mappings/${mappingId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get AOP mapping:', error);
    throw error;
  }
};

/**
 * Create AOP mapping
 * @param {Object} data - Mapping data
 * @returns {Promise<Object>} Created mapping
 */
export const createAopMapping = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/mappings`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create AOP mapping:', error);
    throw error;
  }
};

/**
 * Update AOP mapping
 * @param {string} mappingId - Mapping ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated mapping
 */
export const updateAopMapping = async (mappingId, data) => {
  try {
    const response = await axiosClient.put(`${API_BASE}/mappings/${mappingId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update AOP mapping:', error);
    throw error;
  }
};

/**
 * Delete AOP mapping
 * @param {string} mappingId - Mapping ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteAopMapping = async (mappingId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/mappings/${mappingId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete AOP mapping:', error);
    throw error;
  }
};

/**
 * Add rule to AOP mapping
 * @param {string} mappingId - Mapping ID
 * @param {Object} rule - Rule data
 * @returns {Promise<Object>} Updated mapping
 */
export const addMappingRule = async (mappingId, rule) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/mappings/${mappingId}/rules`, rule);
    return response.data;
  } catch (error) {
    console.error('Failed to add mapping rule:', error);
    throw error;
  }
};

/**
 * Update mapping rule
 * @param {string} mappingId - Mapping ID
 * @param {string} ruleId - Rule ID
 * @param {Object} data - Rule data
 * @returns {Promise<Object>} Updated mapping
 */
export const updateMappingRule = async (mappingId, ruleId, data) => {
  try {
    const response = await axiosClient.put(`${API_BASE}/mappings/${mappingId}/rules/${ruleId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update mapping rule:', error);
    throw error;
  }
};

/**
 * Delete mapping rule
 * @param {string} mappingId - Mapping ID
 * @param {string} ruleId - Rule ID
 * @returns {Promise<Object>} Updated mapping
 */
export const deleteMappingRule = async (mappingId, ruleId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/mappings/${mappingId}/rules/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete mapping rule:', error);
    throw error;
  }
};

// ============================================
// Validation Rules API
// ============================================

/**
 * Get all validation rules
 * @param {Object} params - Query parameters (filters, pagination)
 * @returns {Promise<Object>} Rules and pagination info
 */
export const getValidationRules = async (params = {}) => {
  try {
    const queryParams = qs.stringify(params);
    const response = await axiosClient.get(`${API_BASE}/validation-rules?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get validation rules:', error);
    throw error;
  }
};

/**
 * Get active validation rules by company size
 * @param {string} companySize - Company size
 * @returns {Promise<Object>} Active rules
 */
export const getActiveValidationRules = async (companySize) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/validation-rules/active/${companySize}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get active validation rules:', error);
    throw error;
  }
};

/**
 * Get validation rule by ID
 * @param {string} ruleId - Rule ID
 * @returns {Promise<Object>} Rule details
 */
export const getValidationRuleById = async (ruleId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/validation-rules/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get validation rule:', error);
    throw error;
  }
};

/**
 * Create validation rule
 * @param {Object} data - Rule data
 * @returns {Promise<Object>} Created rule
 */
export const createValidationRule = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/validation-rules`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create validation rule:', error);
    throw error;
  }
};

/**
 * Update validation rule
 * @param {string} ruleId - Rule ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated rule
 */
export const updateValidationRule = async (ruleId, data) => {
  try {
    const response = await axiosClient.put(`${API_BASE}/validation-rules/${ruleId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update validation rule:', error);
    throw error;
  }
};

/**
 * Delete validation rule
 * @param {string} ruleId - Rule ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteValidationRule = async (ruleId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/validation-rules/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete validation rule:', error);
    throw error;
  }
};

/**
 * Validate financial statement data
 * @param {Object} data - Validation data (companySize, bilansStanjaValues, bilansUspehaValues)
 * @returns {Promise<Object>} Validation results
 */
export const validateStatement = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/validate`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to validate statement:', error);
    throw error;
  }
};

/**
 * Download XML file
 * @param {string} statementId - Statement ID
 * @param {string} formType - bilansStanja or bilansUspeha
 * @returns {Promise<void>} Downloads file
 */
export const downloadXml = async (statementId, formType) => {
  try {
    // Use direct download endpoint (XML stored in database, no S3)
    const response = await axiosClient.get(`${API_BASE}/${statementId}/download/${formType}`, {
      responseType: 'blob',
    });

    // Get filename from Content-Disposition header or generate default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${formType}_${statementId}.xml`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        [, filename] = match;
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download XML:', error);
    throw error;
  }
};

// ============================================
// Periodic Reports API (Excel-based)
// ============================================

/**
 * Get formPeriod enum (ANNUAL/MONTHLY/QUARTERLY/SEMIANNUAL/YTD).
 */
export const getFormPeriodsEnum = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/enums/form-periods`);
    return response.data;
  } catch (error) {
    console.error('Failed to get form periods enum:', error);
    throw error;
  }
};

/**
 * Upload BB Excel + meta and preview leaf-AOP values for periodic report.
 * @param {FormData} formData - currentYearFile + companySize + year + formPeriod + periodIndex
 */
export const uploadAndPreviewPeriodic = async (formData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/periodic/preview`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to preview periodic report:', error);
    throw error;
  }
};

/**
 * Generate periodic Excel + persist FinancialStatement.
 * @param {Object} payload - companyId?, companyName?, companySize, year,
 *                           formPeriod, periodIndex, language, accountBalances,
 *                           bilansStanja, bilansUspeha
 */
export const generatePeriodicReport = async (payload) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/periodic/generate`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to generate periodic report:', error);
    throw error;
  }
};

/**
 * Trigger browser download of the persisted periodic Excel.
 */
export const downloadPeriodicExcel = async (statementId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/periodic/${statementId}/download`,
      { responseType: 'blob' }
    );

    let filename = `periodic_${statementId}.xlsx`;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) [, filename] = match;
    }

    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download periodic Excel:', error);
    throw error;
  }
};

